import type { PlanEntitlements, SessionPlan, SessionTenant, SessionUser } from "@models/session";

function str(value: unknown): string | null {
    return value != null && String(value).length > 0 ? String(value) : null;
}

function num(value: unknown, fallback: number): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function limit(value: unknown): number | null {
    return value === null || value === undefined ? null : num(value, 0);
}

export function normalizeEntitlements(raw: unknown): PlanEntitlements | null {
    if (!raw || typeof raw !== "object") return null;
    const r = raw as Record<string, unknown>;
    const limits = (r.limits ?? {}) as Record<string, unknown>;
    return {
        stockManagement: r.stockManagement === true,
        monthlyReports: r.monthlyReports === true,
        weeklyReports: r.weeklyReports === true,
        maxRiders: limit(r.maxRiders ?? limits.maxRiders),
        maxCustomers: limit(r.maxCustomers ?? limits.maxCustomers),
        maxProducts: limit(r.maxProducts ?? limits.maxProducts),
        recommended: r.recommended === true,
        comingSoon: Array.isArray(r.comingSoon) ? (r.comingSoon as string[]) : [],
    };
}

export function normalizePlan(raw: unknown): SessionPlan | null {
    if (!raw || typeof raw !== "object") return null;
    const p = raw as Record<string, unknown>;
    if (!p.id) return null;
    return {
        id: String(p.id),
        name: String(p.name ?? ""),
        key: String(p.key ?? ""),
        cost: String(p.cost ?? "0"),
        discountedAmount: p.discountedAmount != null ? String(p.discountedAmount) : null,
        description: str(p.description),
        features: Array.isArray(p.features) ? (p.features as unknown[]).map(String) : [],
        entitlements: normalizeEntitlements(p.entitlements),
        isActive: p.isActive !== false,
    };
}

function normalizeTenant(raw: unknown): SessionTenant | null {
    if (!raw || typeof raw !== "object") return null;
    const t = raw as Record<string, unknown>;
    if (!t.id) return null;
    return {
        id: String(t.id),
        tenantCode: String(t.tenantCode ?? ""),
        name: str(t.name),
        companyName: str(t.companyName),
        companyLogo: str(t.companyLogo),
        timezoneOffset: num(t.timezoneOffset, 0),
        lowStockThreshold: num(t.lowStockThreshold, 10),
        saasPaidUntil: str(t.saasPaidUntil),
        saasAutoRevokedForNonpayment: t.saasAutoRevokedForNonpayment === true,
        saasSubscriptionRevokedAt: str(t.saasSubscriptionRevokedAt),
        saasSuspendedForInactivePlanAt: str(t.saasSuspendedForInactivePlanAt),
    };
}

export function isTenantRole(role: unknown): boolean {
    return role === "tenant";
}

export function normalizeUser(raw: Record<string, unknown>): SessionUser {
    return {
        id: String(raw.id),
        name: String(raw.name ?? ""),
        email: String(raw.email ?? ""),
        phone: str(raw.phone),
        role: "tenant",
        profilePicture: str(raw.profilePicture),
        companyName: str(raw.companyName),
        city: typeof raw.city === "object" && raw.city ? str((raw.city as { name?: unknown }).name) : str(raw.city),
        address: str(raw.address),
        planId: str(raw.planId),
        plan: normalizePlan(raw.plan),
        tenantId: str(raw.tenantId),
        tenant: normalizeTenant(raw.tenant),
    };
}
